import { useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Popover, SpinnerLoader } from '@kula-ai/components';
import { ChevronDown, Paperclip, Upload01 } from '@kula-ai/icons-core';
// kula-exempt: kula Text takes `variant`/`color` enums only — no `size`
// ("xs-semi-bold", "sm-medium"), no spacing props (px/pt/pb/py) and no
// letterSpacing, all of which these group headings and inline messages need.
// Wrapping each in a Chakra Box purely for spacing would trade one primitive
// for two. A size scale + style-prop passthrough on Text looks like it belongs
// upstream in @kula-ai/components.
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks';
import { useOfferStore } from '../../store';
import { attachFilesKeys } from './attachFilesApi';
import type { AttachableGroup, AttachableItem } from './attachFilesApi';
import type { useOfferAttachments } from './useOfferAttachments';
import { useUploadAttachmentMutation } from '../../api';
import {
  ATTACH_FILES_LOAD_ERROR,
  ATTACHMENT_SIZE_LIMIT_MB,
  GROUP_PLACEHOLDER_COPY,
  SCORECARD_GENERATING_ROW_LABEL,
  approvalAttachmentErrorCopy,
  attachmentSizeWarning,
} from './attachFilesCopy';

const BYTES_PER_MB = 1024 * 1024;

interface AttachFilesPickerProps {
  offerId?: string | number;
  attachments: ReturnType<typeof useOfferAttachments>;
}

// The picker (Design.md `2487:6173`): 3 groups, exact labels come from the
// server (contract seam_1) — never hardcoded here. A group/item with nothing
// behind it renders disabled + visible, never hidden (E1-E5).
export const AttachFilesPicker = ({
  offerId,
  attachments,
}: AttachFilesPickerProps) => {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useShowToast();
  const queryClient = useQueryClient();
  const uploadMutation = useUploadAttachmentMutation();
  // Spans upload + commit as one busy window, so the footer's Send button does
  // not flick back on in the gap between them.
  const setIsAttachingFile = useOfferStore((state) => state.setIsAttachingFile);
  const {
    groups,
    isLoading,
    isError,
    refetch,
    isDraftSelected,
    toggleDraft,
    addUploadedToDraft,
    resetDraft,
    confirmDraft,
    isSaving,
    selectedAttachments,
    draftAttachments,
    setUploadingFile,
    commitUploaded,
  } = attachments;

  // Over the limit blocks the commit: the warning says the approval email may
  // not be delivered, and letting Add through anyway means the recruiter finds
  // out only when an approver never receives it. (V3 originally left Add
  // enabled — cases.md asked only that the warning be *seen* — but an advisory
  // that precedes a silent failure is not much of a warning.) byte_size is
  // required on every AttachableItem/SelectedAttachment (contract.yml seam_1
  // + amendment_6) — no `?? 0` fallback needed, and one would have undercounted
  // a genuinely-missing value instead of surfacing the gap.
  const draftTotalBytes = draftAttachments.reduce(
    (total, item) => total + item.byte_size,
    0
  );
  const overSizeLimit =
    draftTotalBytes / BYTES_PER_MB > ATTACHMENT_SIZE_LIMIT_MB;

  // The query is fetched when this component mounts, and the drawer can stay
  // mounted for a long time — a resume uploaded, a scorecard submitted or a file
  // added elsewhere in the meantime would otherwise show as a stale (often
  // disabled) row until a full reload. Refetching on open costs one request per
  // open and makes the picker always show what would actually be attached.
  const openMenu = () => {
    setOpen(true);
    refetch();
  };

  const closeMenu = (discard: boolean) => {
    if (discard) resetDraft();
    setOpen(false);
  };

  const handleAdd = async () => {
    try {
      await confirmDraft();
      setOpen(false);
    } catch (error) {
      // contract.yml amendment_3b_seam_2_error_shape — the real
      // {errors: [string], field_errors?: {...}} convention. Falls back to
      // the generic title alone for anything that isn't this shape (network
      // error, unexpected response) rather than showing "undefined".
      toast({
        title: 'Failed to save attachments',
        description: approvalAttachmentErrorCopy(error),
        status: 'error',
      });
    }
  };

  const handleUploadChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    // The input clears immediately, so without this guard a second pick while
    // the first PUT is still in flight fires a concurrent upload.
    if (!file || !offerId || uploadMutation.isPending) return;
    // Named from the File object, so the chip can show what is being uploaded
    // straight away — the server tells us nothing until the PUT returns, and a
    // chip reading "Uploading…" says less than the filename the recruiter just
    // picked.
    setUploadingFile({ filename: file.name, byte_size: file.size });
    setIsAttachingFile(true);
    // Closed immediately: the upload is the action, and leaving the list open
    // over a chip that is already showing the file's progress just hides it.
    setOpen(false);
    try {
      // PUT upload_attachment writes to the OFFER's additional_documents, so
      // core resolves it back as `ref_type: "offer_document"` keyed on the
      // ActiveStorage::Blob id (AttachableDocumentsBuilder#offer_document_items,
      // ApprovalAttachmentResolver#resolve_offer_document both key on
      // `blob.id`, not the ActiveStorage::Attachment join-row id) — confirmed
      // against core's own code, not assumed.
      //
      // _attachment.json.jbuilder now extracts `blob_id` alongside `id` (core
      // closed this gap), so the freshly uploaded file can be auto-selected
      // without a round trip. Guard for its absence anyway — a cached/older
      // response without `blob_id` falls back to the previous invalidate-only
      // behaviour (recruiter ticks it manually under OTHER FILES) rather than
      // sending a bad ref built from the wrong id.
      const response = await uploadMutation.mutateAsync({
        id: offerId,
        file,
      });
      const uploaded = response?.data?.attachments?.[0];
      await queryClient.invalidateQueries({
        queryKey: attachFilesKeys.byOffer(offerId),
      });
      if (uploaded?.blob_id !== undefined && uploaded?.blob_id !== null) {
        addUploadedToDraft({
          ref_type: 'offer_document',
          ref_id: String(uploaded.blob_id),
          filename: uploaded.filename || file.name,
          content_type: uploaded.content_type || file.type,
          // Not returned by _attachment.json.jbuilder — the browser's own
          // File.size is the same value core would report for a freshly
          // uploaded blob, and it's only used locally for the V3 size guard.
          byte_size: file.size,
          // Falls back to '' when the response omits it (older/cached
          // jbuilder) rather than guessing — SelectedAttachment.url is a
          // required string, and `canPreview` now treats an explicit '' as
          // "nothing to preview" (previewSupport.ts), so this no longer
          // renders a 👁 that opens a blank preview.
          url: uploaded.url || '',
        });
        // The popover is closed, so there is no "Add" left to press — the
        // upload has to commit itself, or the file would sit selected and
        // never reach the approval email. Only the uploaded file is committed:
        // any unsaved ticks stay unsaved, exactly as they would have if the
        // recruiter had never uploaded.
        await commitUploaded({
          ref_type: 'offer_document',
          ref_id: String(uploaded.blob_id),
          filename: uploaded.filename || file.name,
          content_type: uploaded.content_type || file.type,
          byte_size: file.size,
          url: uploaded.url || '',
        });
        toast({ title: 'File attached' });
      } else {
        // No blob_id, so nothing was auto-selected; the file only appears as an
        // OTHER FILES row after the refetch and still needs ticking.
        toast({
          title: 'File uploaded',
          description: 'Select it under Other files, then click Add.',
        });
      }
    } catch (error) {
      toast({
        title: 'File upload failed',
        description: approvalAttachmentErrorCopy(error),
        status: 'error',
      });
    } finally {
      // Cleared whatever happened: on failure the chip must stop implying an
      // upload is still running.
      setUploadingFile(null);
      setIsAttachingFile(false);
    }
  };

  // The row's accessible name comes from Checkbox's own `label` (a real
  // <label htmlFor>, unlike a sibling KText) — the row's click/keyboard
  // handling is the native input's, not a hand-rolled Flex onClick.
  const renderRow = (group: AttachableGroup, item: AttachableItem) => {
    const disabled = group.disabled || !!item.disabled;
    // AMENDMENT 9 (D-E) — the scorecards row while its PDF renders
    // (Gotenberg, ~3-15s). This row has no separate Preview/Cancel controls
    // today — the checkbox IS its only interactive element — so the loader
    // replaces that one control rather than sitting beside affordances that
    // don't exist here yet. `unavailable` (no completed scorecards) is
    // deliberately NOT handled here — it falls through to the plain disabled
    // checkbox below, same as it always has (it is not a loading state).
    const isGenerating = item.status === 'generating';
    return (
      <Flex
        key={`${item.ref_type}:${item.ref_id ?? ''}`}
        align="center"
        h="44px"
        px="12px"
        gap="8px"
        borderBottom="1px solid"
        borderColor="secondary-100"
        data-test-id={
          isGenerating ? `attach-row-generating-${item.ref_type}` : undefined
        }
      >
        {isGenerating ? (
          <>
            <SpinnerLoader
              size="sm"
              data-test-id="attach-row-generating-spinner"
            />
            <KText size="sm-regular" color="secondary-500">
              {item.filename} — {SCORECARD_GENERATING_ROW_LABEL}
            </KText>
          </>
        ) : (
          <Checkbox
            size="sm"
            checked={isDraftSelected(item)}
            disabled={disabled}
            onChange={() => !disabled && toggleDraft(item)}
            label={item.filename}
            labelClassName={`!text-sm !font-normal ${
              disabled ? '!text-secondary-400' : '!text-secondary-700'
            }`}
          />
        )}
      </Flex>
    );
  };

  const renderGroup = (group: AttachableGroup) => {
    const headingId = `attach-group-heading-${group.key}`;
    return (
      <Box
        key={group.key}
        data-test-id={`attach-group-${group.key}`}
        role="group"
        aria-labelledby={headingId}
      >
        {/* Its own banded row, not a label floating above the first option:
            the fill is what separates one group from the rows of the group
            before it. */}
        <KText
          id={headingId}
          size="xs-semi-bold"
          color={group.disabled ? 'secondary-400' : 'secondary-600'}
          bg="secondary-25"
          px="12px"
          py="10px"
          textTransform="uppercase"
          letterSpacing="0.06em"
          data-disabled={group.disabled ? 'true' : 'false'}
        >
          {group.label}
        </KText>
        {group.items.length === 0 ? (
          <Flex
            align="center"
            h="44px"
            px="12px"
            borderBottom="1px solid"
            borderColor="secondary-100"
          >
            <Checkbox
              size="sm"
              checked={false}
              disabled
              label={GROUP_PLACEHOLDER_COPY[group.key]}
              labelClassName="!text-sm !font-normal !text-secondary-400"
            />
          </Flex>
        ) : (
          group.items.map((item) => renderRow(group, item))
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* A native, never-rendered file trigger — no kula-ai/Chakra Input, same
          pattern as ViewFile.tsx's own hidden replace-file input. There is no
          themed "UI" here to source from a package. */}
      <input
        ref={fileInputRef}
        type="file"
        // Same set as this feature's sibling upload (offer-template/
        // AddMoreFilesButton). Unrestricted here would let a recruiter attach
        // e.g. a .zip, which then ships as a real MIME attachment on the
        // approval email once blob_id auto-selects it.
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        aria-label="Upload new document"
        onChange={handleUploadChange}
        style={{ display: 'none' }}
      />
      <Popover
        open={open}
        onOpenChange={(nextOpen: boolean) =>
          nextOpen ? openMenu() : closeMenu(true)
        }
        align="end"
        side="bottom"
        className="p-0 overflow-hidden"
        trigger={
          // Sits in the editor's toolbar row now, not the card header, so it
          // reads as a toolbar control (link-styled, no button chrome) beside
          // the formatting icons rather than as a second header action.
          // kula Button has no unstyled/link variant — every variant carries a
          // background (Button.variants.js) — so the chrome is stripped via
          // className. A `link`/`ghost` variant looks like it belongs upstream
          // in @kula-ai/components.
          <Button
            variant="tertiary"
            size="sm"
            className="!h-auto !bg-transparent !px-0 !text-primary-600 hover:!bg-transparent hover:!text-primary-700"
            leftElement={<Paperclip size={16} />}
            data-test-id="attach-files-trigger"
          >
            <Flex align="center" gap="6px">
              <Box position="relative">
                <KText size="sm-medium" color="inherit">
                  Attach files
                </KText>
                {selectedAttachments.length > 0 && (
                  // Rides the label's top-right corner so the chevron stays put
                  // beside the label instead of being pushed along as the count
                  // grows. minW + px let a two-digit count widen rather than
                  // clip. kula Badge sits inline only — it has no corner/dot
                  // placement, which looks like it belongs upstream in
                  // @kula-ai/components.
                  <Flex
                    position="absolute"
                    // Figma 2498:1313: the badge box spans 4345-4361 against a
                    // label ending at 4353 and topped at 176 — so it overlaps
                    // the last letter by 8px and rides 5px above the cap line.
                    // Pulling it further out (-10/-10) is what leaves it
                    // floating clear of the word instead of sitting on it.
                    top="-5px"
                    right="-8px"
                    minW="16px"
                    h="16px"
                    px="2px"
                    align="center"
                    justify="center"
                    borderRadius="full"
                    bg="primary-500"
                    color="base-white"
                    fontSize="10px"
                    fontWeight="normal"
                    lineHeight="16px"
                    border="1px solid"
                    borderColor="base-white"
                    data-test-id="attach-files-count"
                  >
                    {selectedAttachments.length}
                  </Flex>
                )}
              </Box>
              <ChevronDown size={16} />
            </Flex>
          </Button>
        }
      >
        <Box w="295px" data-test-id="attach-files-picker">
          {isLoading ? (
            <Flex align="center" justify="center" py="24px">
              <SpinnerLoader size="sm" data-test-id="attach-files-loading" />
            </Flex>
          ) : isError ? (
            <Flex direction="column" gap="8px" p="12px">
              <KText size="sm-regular" color="ats-negative">
                {ATTACH_FILES_LOAD_ERROR}
              </KText>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => refetch()}
                data-test-id="attach-files-retry"
              >
                Retry
              </Button>
            </Flex>
          ) : (
            <Box maxH="280px" overflowY="auto">
              {groups.map(renderGroup)}
            </Box>
          )}
          {overSizeLimit && (
            // Ruled off from the option rows above: without it the warning
            // reads as belonging to the last row it happens to sit under,
            // rather than to the selection as a whole.
            <KText
              size="xs-regular"
              color="ats-negative"
              px="12px"
              py="8px"
              borderTopWidth="1px"
              borderTopStyle="solid"
              borderTopColor="secondary-100"
              data-test-id="attach-files-size-warning"
            >
              {attachmentSizeWarning(ATTACHMENT_SIZE_LIMIT_MB)}
            </KText>
          )}
          <Box borderTop="1px solid" borderColor="secondary-100">
            {/* kula Button has no unstyled/list-row variant — every variant
                carries a colored background (Button.variants.js). Overriding
                the chrome via className to match the plain-row design until a
                row/list-item variant exists upstream. Was a Flex+onClick with
                no role/tabIndex/key handler — unreachable by keyboard, which
                broke E5 ("Upload still works when everything else is
                disabled") for anyone not using a mouse. */}
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              className="!w-full !h-11 !justify-start !gap-2 !rounded-none !border-none !bg-transparent !px-3 !text-sm !font-normal !text-primary-500 hover:!bg-secondary-25"
              leftElement={<Upload01 size={16} />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? 'Uploading…' : 'Upload new document'}
            </Button>
          </Box>
          <Flex
            justify="flex-end"
            gap="8px"
            p="10px 12px"
            borderTop="1px solid"
            borderColor="secondary-100"
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={() => closeMenu(true)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSaving}
              disabled={overSizeLimit}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Flex>
        </Box>
      </Popover>
    </Box>
  );
};
